import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ContentCard from "../misc/ContentCard";
import CustomButton from "../CustomButton";
import Text from "../../styleguide/Text";
import TableGlobalFilter from "./components/TableGlobalFilter";
import TableGlobalSearch from "./components/TableGlobalSearch";
import TableHeader from "./components/TableHeader";
import TableHeaderCategories from "./components/TableHeaderCategories";
import { TableLoading } from "./components/TableLoading";
import TablePagination from "./components/TablePagination";
import useTableConfig from "./hooks/useTableConfig";
import useTableProgressiveRows from "./hooks/useTableProgressiveRows";
import SectionTableBody from "./sectionTable/SectionTableBody";
import SectionTableTitle from "./sectionTable/SectionTableTitle";
import {
  TableSectionActionsProps,
  TableSectionData,
  TableSectionView,
} from "./sectionTable/sectionTable.interface";
import {
  getTableSectionsItems,
  getTableSectionsView,
} from "./sectionTable/sectionTableHelper";
import {
  TableActions,
  TableData,
  TableFilterDataItem,
  TableHeaderCategoryConfig,
} from "./tableContext/TableContext";
import TableProvider from "./tableContext/TableProvider";
import useTableCtx from "./tableContext/useTableCtx";

type Props = {
  className?: string;
  tableHeaderData: TableHeaderCategoryConfig[];
  tableSectionsData: TableSectionData[];
  globalFilter?: TableFilterDataItem[];
  withGlobalSearch?:
    | boolean
    | {
        placeholder?: string;
        searchDelay?: number;
      };
  withGlobalFilter?: boolean;
  borderedRow?: boolean;
  ActionButtonsComponent?: any;
  SectionActionsComponent?: React.ComponentType<TableSectionActionsProps>;
  CustomHeaderComponent?: any;
  tableTitle?: string;
  showSearchForCategories?: boolean;
  withTablePagination?: boolean;
  withRouteParams?: boolean;
  withSeparateSections?: boolean;
  renderBatchSize?: number;
  handleTableActions?: (value: TableActions) => void;
  handleUnmount?: () => void;
  tableLoading?: boolean;
  totalItems?: number;
  emptyTableLabel?: string;
};

const SectionTableWidget: React.FC<Props> = (props) => {
  const [searchRouteParams] = useSearchParams();

  const initialTablePagination = useMemo(() => {
    const page = searchRouteParams.get("page");
    const rowsPerPage = searchRouteParams.get("rowsPerPage");
    if (!page && !rowsPerPage) return undefined;
    return {
      page: page ? parseInt(page) : 1,
      rowsPerPage: rowsPerPage ? parseInt(rowsPerPage) : 25,
    };
  }, [searchRouteParams]);

  const initialTableSearch = useMemo(() => {
    const search: Record<string, string> = {};
    searchRouteParams.forEach((value, key) => {
      if (key !== "page" && key !== "rowsPerPage") {
        search[key] = value;
      }
    });
    return Object.keys(search).length > 0 ? search : undefined;
  }, [searchRouteParams]);

  return (
    <TableProvider
      initialTableSearch={initialTableSearch}
      initialTablePagination={initialTablePagination}>
      <SectionTableComponent {...props} />
    </TableProvider>
  );
};

const SectionTableComponent: React.FC<Props> = ({
  globalFilter,
  tableHeaderData,
  tableSectionsData,
  className,
  withGlobalSearch,
  withGlobalFilter,
  borderedRow = true,
  ActionButtonsComponent,
  SectionActionsComponent,
  CustomHeaderComponent,
  tableTitle,
  showSearchForCategories = true,
  withTablePagination = true,
  withRouteParams = true,
  withSeparateSections = true,
  renderBatchSize,
  handleTableActions,
  handleUnmount,
  tableLoading,
  totalItems,
  emptyTableLabel = "Tabelul este gol",
}) => {
  const allTableItems = useMemo(
    () => getTableSectionsItems(tableSectionsData),
    [tableSectionsData],
  );
  const { tableData, tableHeader } = useTableCtx()!;
  const rowsById = useMemo(
    () =>
      new Map<TableData["id"], TableData>(
        tableData.map((row) => [row.id, row]),
      ),
    [tableData],
  );
  const { visibleRowCount, hasMoreRows, loadMoreRef, handleLoadMore } =
    useTableProgressiveRows({
      totalRows: allTableItems.length,
      renderBatchSize,
      tableLoading:
        tableLoading || (allTableItems.length > 0 && tableData.length === 0),
    });
  const visibleSections = useMemo(
    () => getTableSectionsView(tableSectionsData, visibleRowCount),
    [tableSectionsData, visibleRowCount],
  );
  const columnCount = tableHeader.length + (ActionButtonsComponent ? 1 : 0);

  useTableConfig({
    handleTableActions,
    handleUnmount,
    globalFilter,
    tableHeaderData,
    tableItemsData: allTableItems,
    withRouteParams,
  });

  const renderTable = (
    sections: TableSectionView[],
    showSectionTitles = true,
  ) => (
    <table
      className={`w-full border-collapse align-middle [&_td]:px-3 [&_tbody_td]:py-4 [&_th]:px-3 [&_thead>tr:first-child:empty]:hidden [&_thead>tr:last-child]:border-b [&_thead>tr:last-child]:border-slate-100 [&_thead>tr:last-child]:bg-surface-hover [&_thead>tr:last-child]:text-muted-dark ${
        borderedRow ? "[&_tbody_tr]:border-b [&_tbody_tr]:border-[#f5f8fa]" : ""
      }`}>
      {!showSectionTitles && sections[0] && (
        <caption className="border-b border-brand/10 bg-brand-subtle px-3 py-5 text-left">
          <SectionTableTitle
            section={sections[0].section}
            isRoot
            SectionActionsComponent={SectionActionsComponent}
          />
        </caption>
      )}
      <TableHeaderCategories
        showActionButtons={!!ActionButtonsComponent}
        showSearchForCategories={showSearchForCategories}
      />
      <SectionTableBody
        sections={sections}
        rowsById={rowsById}
        columnCount={columnCount}
        showSectionTitles={showSectionTitles}
        ActionButtonsComponent={ActionButtonsComponent}
        SectionActionsComponent={SectionActionsComponent}
      />
    </table>
  );

  const renderEmptyTable = () => (
    <div className="sticky left-0 flex w-full items-center justify-center py-12 text-center">
      <Text variant="body1" className="!text-2xl font-semibold text-slate-800">
        {emptyTableLabel}
      </Text>
    </div>
  );

  return (
    <ContentCard
      cardBodyClassName={`relative w-full ${className ?? ""}`}
      cardBodyStyle={{ padding: 0, margin: 0 }}
      cardHeaderClassName="border-0 !pt-0 pb-4 pl-1 text-left"
      cardTitleStyle={{ marginBottom: 0 }}
      cardTitle={tableTitle}>
      {(withGlobalSearch || withGlobalFilter) && (
        <div className="mb-6 flex">
          {withGlobalSearch && (
            <TableGlobalSearch withGlobalSearch={withGlobalSearch} />
          )}
          {withGlobalFilter && <TableGlobalFilter />}
        </div>
      )}
      <TableHeader CustomHeaderComponent={CustomHeaderComponent} />
      {withSeparateSections ? (
        <div className="space-y-6">
          {visibleSections.map((section) => (
            <div
              key={section.section.id}
              className="overflow-hidden rounded-card border border-slate-200">
              <div className="overflow-x-auto">
                {renderTable([section], false)}
                {section.rowCount === 0 && renderEmptyTable()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {renderTable(visibleSections)}
          {allTableItems.length === 0 && renderEmptyTable()}
        </div>
      )}
      {withSeparateSections &&
        tableSectionsData.length === 0 &&
        renderEmptyTable()}
      {hasMoreRows && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          <CustomButton
            variant="outlined"
            onClick={handleLoadMore}
            disabled={tableLoading}>
            Afiseaza mai multe
          </CustomButton>
        </div>
      )}
      {withTablePagination && <TablePagination totalItems={totalItems} />}
      {tableLoading && <TableLoading />}
    </ContentCard>
  );
};

export default SectionTableWidget;
